import React, { useState, useRef, useEffect } from 'react';
import { 
  Activity,
  Server,
  Database,
  Container,
  ChevronLeft,
  ChevronRight,
  Icon,
  Scale,
  ScreenShare,
  ShieldCheck,
} from 'lucide-react';
import MetricCard from './MetricCard';
import ExpandedView from './ExpandedView';
import { bucket,layoutListMove  } from '@lucide/lab';

const getIconComponent = (namespace) => {
  if (!namespace) return <Activity className="h-6 w-6 text-gray-800" />;
  
  const namespaceLower = namespace.toLowerCase();
  
  if (namespaceLower.includes('lambda')) {
    return <Server className="h-6 w-6 text-[#003366]" />;
  } else if (namespaceLower.includes('rds') ) {
    return  <Database className="h-6 w-6 text-[#003366]" />;
  } else if (namespaceLower.includes('ecs') ) {
    return <Container className="h-6 w-6 text-[#003366]" />;
  } else if (namespaceLower.includes('sqs') ) {
    return <Icon iconNode={layoutListMove } className="h-6 w-6 text-[#003366]" />;
  } else if (namespaceLower.includes('cloudfront') ) {
    return <ScreenShare className="h-6 w-6 text-[#003366]" />;
  } else if (namespaceLower.includes('applicationelb') ) {
    return <Scale className="h-6 w-6 text-[#003366]" />;
  } else if (namespaceLower.includes('s3') ) {
    return <Icon iconNode={bucket} className="h-6 w-6  text-[#003366]"/>;
  } else if (namespaceLower.includes('wafv2') ) {
    return <ShieldCheck   className="h-6 w-6 text-[#003366]" />;
  } 
  
  return <Activity className="h-6 w-6 text-gray-800" />;
};

const GraphLayout = ({ dashboardData, selectedTime, api }) => {
  const [canScrollLeft, setCanScrollLeft] = useState({});
  const [canScrollRight, setCanScrollRight] = useState({});
  const [isHovering, setIsHovering] = useState(null);
  const [expandedChart, setExpandedChart] = useState(null);
  const [containerWidths, setContainerWidths] = useState({});
  
  const scrollContainerRefs = useRef({});
  const containerRef = useRef(null);
  
  const MAX_CARDS_PER_ROW = 4;

  const calculateCardWidth = (index, metricsCount) => {
    const containerWidth = containerWidths[index];
    
    if (!containerWidth) return '100%';
    
    if (metricsCount <= MAX_CARDS_PER_ROW) {
      const gapSpace = (metricsCount - 1) * 16;
      const availableWidth = containerWidth - gapSpace;
      const cardWidth = Math.floor(availableWidth / metricsCount);
      return `${cardWidth}px`;
    }
    
    return '400px';
  };

  const checkScrollability = (index) => {
    const container = scrollContainerRefs.current[index];
    if (container) {
      setCanScrollLeft((prev) => ({
        ...prev,
        [index]: container.scrollLeft > 0,
      }));

      setCanScrollRight((prev) => ({
        ...prev,
        [index]:
          container.scrollLeft <
          container.scrollWidth - container.clientWidth - 5,
      }));
    }
  };

  useEffect(() => {
    if (!dashboardData || !dashboardData.details) return;
    
    const updateContainerWidths = () => {
      dashboardData.details.forEach((namespaceObj, index) => {
        const container = scrollContainerRefs.current[`${index}-0`];
        if (container) {
          setContainerWidths(prev => ({
            ...prev,
            [`${index}`]: container.clientWidth
          }));
        }
      });
    };
    
    setTimeout(updateContainerWidths, 200);
    
    setTimeout(() => {
      dashboardData.details.forEach((namespaceObj, index) => {
        checkScrollability(`${index}-0`);
      });
    }, 300);

    const handleResize = () => {
      updateContainerWidths();
      
      dashboardData.details.forEach((namespaceObj, index) => {
        checkScrollability(`${index}-0`);
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [dashboardData]);

  const scroll = (index, direction) => {
    const container = scrollContainerRefs.current[index];
    if (container) {
      const containerWidth = container.clientWidth;
      const cardWidth = containerWidth / Math.min(MAX_CARDS_PER_ROW, container.children.length);
      const scrollAmount = cardWidth * (direction === "left" ? -1 : 1);

      container.scrollBy({
        left: scrollAmount,
        behavior: "smooth",
      });

      setTimeout(() => checkScrollability(index), 300);
    }
  };

  const toggleExpandedChart = (namespaceObj, metricName, resourceNames, metricData) => {
    setExpandedChart({
      namespace: namespaceObj.namespace,
      metricName: metricName,
      resourceNames: resourceNames,
      metricData: metricData
    });
  };

  const handleCloseExpandedChart = () => {
    setExpandedChart(null);
  };

  if (!dashboardData || !dashboardData.details) {
    return <div className="text-center p-8">No dashboard data available</div>;
  }
  return (
    <div ref={containerRef} className="space-y-4 bg-gray-50 border-r-5 p-3">
      {expandedChart && (
        <ExpandedView
          namespace={expandedChart.namespace}
          metricName={expandedChart.metricName}
          resourceNames={expandedChart.resourceNames}
          api={api}
          initialSelectedTime={selectedTime}
          dashboardData={dashboardData}
          onClose={handleCloseExpandedChart}
          metricData={expandedChart.metricData}
        />
      )}

      {dashboardData.details.map((namespaceObj, index) => (
        <div key={index} className="mb-8">
          <div className="flex items-center gap-2 mb-2 ">
            {getIconComponent(namespaceObj.namespace)}
            <h2 className="text-xl font-semibold text-gray-800">
              {namespaceObj.namespace}
            </h2>
          </div>

          <div 
            className="relative group mb-4"
            onMouseEnter={() => setIsHovering(`${index}-0`)}
            onMouseLeave={() => setIsHovering(null)}
          >
            <button
              onClick={() => scroll(`${index}-0`, "left")}
              className={`absolute -left-4 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full 
                        bg-white/80 shadow-lg hover:bg-white/90 transition-all duration-300
                        backdrop-blur-sm ${
                          canScrollLeft[`${index}-0`]
                            ? isHovering === `${index}-0`
                              ? "opacity-100"
                              : "opacity-0 group-hover:opacity-100"
                            : "opacity-0"
                        } hover:scale-110`}
              disabled={!canScrollLeft[`${index}-0`]}
              style={{ transform: "translate(-50%, -50%)" }}
              aria-label="Scroll left"
            >
              <ChevronLeft className="h-6 w-6 text-gray-600" />
            </button>

            <div
              ref={(el) => (scrollContainerRefs.current[`${index}-0`] = el)}
              className="flex overflow-x-auto gap-4 scroll-smooth p-2 no-scrollbar"
              onScroll={() => checkScrollability(`${index}-0`)}
              style={{
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                scrollBehavior: "smooth",
              }}
            >
              
              {namespaceObj.metrics && namespaceObj.metrics.map((metric, metricIndex) => {
                
                const adaptiveWidth = calculateCardWidth(index, namespaceObj.metrics.length);
                return (
                  <div
                    key={metricIndex}
                    className="flex-none bg-white rounded-lg shadow p-4"
                    style={{
                      width: adaptiveWidth,
                      minWidth: '240px',
                      maxWidth: '100%'
                    }}
                  >
                    <MetricCard
                      namespace={namespaceObj.namespace}
                      metricName={metric.metricName}
                      resourceNames={namespaceObj.resourceName}
                      api={api}
                      selectedTime={selectedTime}
                      dashboardData={dashboardData}
                      onExpand={(metricData) => toggleExpandedChart(namespaceObj, metric.metricName, namespaceObj.resourceName, metricData)}
                      height={160}
                    />
                  </div>
                );
              })}
            </div>

            <button
              onClick={() => scroll(`${index}-0`, "right")}
              className={`absolute -right-4 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full 
                        bg-white/80 shadow-lg hover:bg-white/90 transition-all duration-300
                        backdrop-blur-sm ${
                          canScrollRight[`${index}-0`]
                            ? isHovering === `${index}-0`
                              ? "opacity-100"
                              : "opacity-0 group-hover:opacity-100"
                            : "opacity-0"
                        } hover:scale-110`}
              disabled={!canScrollRight[`${index}-0`]}
              style={{ transform: "translate(50%, -50%)" }}
              aria-label="Scroll right"
            >
              <ChevronRight className="h-6 w-6 text-gray-600" />
            </button>
          </div>
        </div>
      ))}
      
      <style jsx>{`
        .no-scrollbar {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-fade-in {
          animation: fadeIn 0.3s ease-in;
        }
      `}</style>
    </div>
  );
};

export default GraphLayout;